import React, { useState, useEffect } from "react";
import { RefreshCw, Loader2, CheckCircle2, XCircle, Smartphone, Globe, Webhook, ClipboardList, AlertTriangle, ArrowDownToLine, KeyRound, Link2, Eye, EyeOff, Save, PlugZap, History, Clock } from "lucide-react";
import { getAppSyncStatus, runAppSync, getSyncAudit, getAppConfig, saveAppConfig, testAppConfig, getAppConfigHistory } from "../../api";
import { useToast } from "../../hooks/use-toast";

const Card = ({ children, className = "" }) => <div className={`bg-white rounded-2xl border border-[#f0ece5] p-5 ${className}`}>{children}</div>;

const SourceRow = ({ label, data }) => {
  const app = data?.app || 0;
  const web = (data?.website || 0) + (data?.import || 0);
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#faf7f2] last:border-0">
      <span className="text-[13px] text-[#4a4a4a]">{label}</span>
      <div className="flex items-center gap-4 text-[12px]">
        <span className="inline-flex items-center gap-1 text-[#0A66C2]"><Smartphone size={13} /> {app} from app</span>
        <span className="inline-flex items-center gap-1 text-[#b8935f]"><Globe size={13} /> {web} from website</span>
      </div>
    </div>
  );
};

const AdminAppSync = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);

  // App connection config
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [interval, setIntervalMin] = useState(15);
  const [showKey, setShowKey] = useState(false);
  const [keyTouched, setKeyTouched] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => { try { const h = await getAppConfigHistory(); setHistory(h.events || []); } catch { /* noop */ } };

  const loadCfg = async () => {
    try {
      const c = await getAppConfig();
      setCfg(c);
      setBaseUrl(c.baseUrl || "");
      setWebhookUrl(c.webhookUrl || "");
      setIntervalMin(c.syncIntervalMinutes || 15);
      setApiKey("");
      setKeyTouched(false);
    } catch { /* non-fatal */ }
    loadHistory();
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = { baseUrl, apiKey: keyTouched ? apiKey : null, webhookUrl, syncIntervalMinutes: interval };
      const r = await saveAppConfig(payload);
      toast({ title: "App connection saved", description: r.baseUrl ? `Base URL: ${r.baseUrl}` : "Cleared." });
      await loadCfg();
      load();
    } catch { toast({ title: "Couldn't save", description: "Please try again." }); }
    finally { setSavingCfg(false); }
  };

  const testCfg = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { baseUrl, apiKey: keyTouched ? apiKey : null };
      const r = await testAppConfig(payload);
      setTestResult(r);
      loadHistory();
      toast({ title: r.reachable ? "App reachable ✓" : "App unreachable ✗", description: r.reachable ? `${r.listings ?? 0} listing(s) visible.` : (r.error || "Check the URL and key.") });
    } catch { setTestResult({ reachable: false, error: "Request failed." }); toast({ title: "Test failed" }); }
    finally { setTesting(false); }
  };

  const load = async () => { setLoading(true); try { setStatus(await getAppSyncStatus()); } catch { toast({ title: "Couldn't load sync status" }); } finally { setLoading(false); } };
  useEffect(() => { load(); loadCfg(); }, []);

  const sync = async () => {
    setSyncing(true);
    try { const r = await runAppSync(); toast({ title: "Sync complete", description: `${r.detail?.synced ?? 0} of ${r.detail?.appTotal ?? "?"} app listings pulled in.` }); load(); if (audit) runAudit(); }
    catch { toast({ title: "Sync failed", description: "Check the app backend is reachable." }); }
    finally { setSyncing(false); }
  };

  const runAudit = async () => {
    setAuditing(true);
    try { setAudit(await getSyncAudit()); }
    catch { toast({ title: "Audit failed", description: "Couldn't reach the app to compare listings." }); }
    finally { setAuditing(false); }
  };

  const renderConnectionPanel = () => (
    <Card data-testid="app-connection-panel">
      <div className="flex items-center gap-2 mb-1"><PlugZap size={16} className="text-[#b8935f]" /><h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">App connection</h4></div>
      <p className="text-[13px] text-[#8a8a8a] mb-4">Point the website at your Anadear mobile app backend. Saved here overrides server defaults instantly — no redeploy needed.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[12px] font-500 text-[#4a4a4a] mb-1.5 flex items-center gap-1.5"><Link2 size={13} /> App API base URL</label>
          <input
            data-testid="app-config-base-url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://api.anadear-app.com"
            className="w-full px-3.5 py-2.5 rounded-xl border border-[#e2d8c8] text-[14px] text-[#2b2b2b] focus:border-[#b8935f] focus:outline-none transition-colors"
          />
          {cfg?.source === "env" && cfg?.envBaseUrl && !cfg?.usingDbBaseUrl && (
            <p className="text-[11px] text-[#a0a0a0] mt-1">Using server default: {cfg.envBaseUrl}</p>
          )}
        </div>
        <div>
          <label className="text-[12px] font-500 text-[#4a4a4a] mb-1.5 flex items-center gap-1.5"><KeyRound size={13} /> App API key</label>
          <div className="relative">
            <input
              data-testid="app-config-api-key"
              type={showKey ? "text" : "password"}
              value={keyTouched ? apiKey : ""}
              onChange={(e) => { setKeyTouched(true); setApiKey(e.target.value); }}
              placeholder={cfg?.keyConfigured ? `Saved (${cfg.keyMasked}) — leave blank to keep` : "Enter API key"}
              className="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-[#e2d8c8] text-[14px] text-[#2b2b2b] focus:border-[#b8935f] focus:outline-none transition-colors"
            />
            <button type="button" onClick={() => setShowKey((v) => !v)} data-testid="app-config-toggle-key" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a0a0a0] hover:text-[#4a4a4a]">
              {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-[11px] text-[#a0a0a0] mt-1">{cfg?.keyConfigured ? "A key is stored. Type a new one only to change it." : "No key set — some apps require one."}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="text-[12px] font-500 text-[#4a4a4a] mb-1.5 flex items-center gap-1.5"><Webhook size={13} /> App webhook URL <span className="text-[#a0a0a0] font-400">(where we push listing/lead changes)</span></label>
          <input
            data-testid="app-config-webhook-url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://api.anadear-app.com/webhooks/website"
            className="w-full px-3.5 py-2.5 rounded-xl border border-[#e2d8c8] text-[14px] text-[#2b2b2b] focus:border-[#b8935f] focus:outline-none transition-colors"
          />
        </div>
        <div>
          <label className="text-[12px] font-500 text-[#4a4a4a] mb-1.5 flex items-center gap-1.5"><Clock size={13} /> Auto-sync every</label>
          <select
            data-testid="app-config-interval"
            value={interval}
            onChange={(e) => setIntervalMin(Number(e.target.value))}
            className="w-full px-3.5 py-2.5 rounded-xl border border-[#e2d8c8] text-[14px] text-[#2b2b2b] focus:border-[#b8935f] focus:outline-none transition-colors bg-white"
          >
            {[[5, "5 minutes"], [15, "15 minutes"], [30, "30 minutes"], [60, "1 hour"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <p className="text-[11px] text-[#a0a0a0] mt-1">How often the website pulls the app's latest listings.</p>
        </div>
      </div>
      {testResult && (
        <div data-testid="app-config-test-result" className={`mt-4 rounded-xl border p-3.5 flex items-start gap-2.5 ${testResult.reachable ? "bg-green-50 border-green-100" : "bg-red-50 border-red-100"}`}>
          {testResult.reachable ? <CheckCircle2 size={18} className="text-green-600 mt-0.5 shrink-0" /> : <XCircle size={18} className="text-red-500 mt-0.5 shrink-0" />}
          <div className="text-[13px]">
            {testResult.reachable
              ? <span className="text-green-700">Reachable — the app currently shows <b>{testResult.listings ?? 0}</b> listing(s).</span>
              : <span className="text-red-600">Unreachable — {testResult.error || "check the URL and API key."}</span>}
            {testResult.endpoint && <span className="block text-[11px] text-[#a0a0a0] font-mono mt-0.5">{testResult.endpoint}</span>}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button onClick={saveCfg} disabled={savingCfg} data-testid="app-config-save-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
          {savingCfg ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save connection
        </button>
        <button onClick={testCfg} disabled={testing} data-testid="app-config-test-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#e2d8c8] text-[#2b2b2b] text-[14px] font-500 hover:border-[#b8935f] transition-colors disabled:opacity-60">
          {testing ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />} Test connection
        </button>
        {cfg && <span className="text-[11px] text-[#a0a0a0] ml-1">Active source: <b className="text-[#4a4a4a]">{cfg.source === "database" ? "saved here" : "server default"}</b></span>}
      </div>
      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#f0ece5]" data-testid="app-connection-history">
          <p className="text-[12px] font-500 text-[#4a4a4a] mb-2 flex items-center gap-1.5"><History size={14} className="text-[#b8935f]" /> Connection history</p>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-lg hover:bg-[#faf7f2] text-[12px]">
                <span className="flex items-center gap-2 min-w-0">
                  {h.reachable ? <CheckCircle2 size={13} className="text-green-600 shrink-0" /> : <XCircle size={13} className="text-red-500 shrink-0" />}
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#f0ece5] text-[#6a6a6a] shrink-0">{h.kind}</span>
                  <span className="text-[#4a4a4a] truncate">{h.detail}</span>
                </span>
                <span className="text-[#a0a0a0] shrink-0">{new Date(h.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );

  if (loading) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading sync status…</div>;

  if (!status) return (
    <div className="space-y-6" data-testid="admin-app-sync">
      <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 text-[13px] text-amber-700" data-testid="app-sync-status-error">
        Couldn't load the live sync status just now. You can still edit and test the app connection below.
        <button onClick={load} className="ml-2 underline">Retry</button>
      </div>
      {renderConnectionPanel()}
    </div>
  );

  const lastAt = status.lastSync?.at ? new Date(status.lastSync.at).toLocaleString("en-GB") : "never";

  return (
    <div className="space-y-6" data-testid="admin-app-sync">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-[20px] font-500 text-[#2b2b2b]">App Sync</h3>
          <p className="text-[13px] text-[#8a8a8a] mt-1">Live status of the two-way link between the website and the Anadear mobile app.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runAudit} disabled={auditing} data-testid="app-sync-audit-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#e2d8c8] text-[#2b2b2b] text-[14px] font-500 hover:border-[#b8935f] transition-colors disabled:opacity-60">
            {auditing ? <Loader2 size={16} className="animate-spin" /> : <ClipboardList size={16} />} Run audit
          </button>
          <button onClick={sync} disabled={syncing} data-testid="app-sync-now-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Sync now
          </button>
        </div>
      </div>

      {renderConnectionPanel()}

      {audit && (
        <Card className="!p-0 overflow-hidden" >
          <div className="p-5 border-b border-[#f0ece5]" data-testid="app-sync-audit">
            <div className="flex items-center gap-2 mb-3"><ClipboardList size={16} className="text-[#b8935f]" /><h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Listing sync audit</h4></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl bg-[#faf7f2] border border-[#f0ece5] p-3 text-center"><p className="font-display text-[24px] text-[#2b2b2b]">{audit.counts.appPublished}</p><p className="text-[11px] text-[#a0a0a0] uppercase">App publishes</p></div>
              <div className="rounded-xl bg-green-50 border border-green-100 p-3 text-center"><p className="font-display text-[24px] text-green-700">{audit.counts.synced}</p><p className="text-[11px] text-green-700/70 uppercase">Synced</p></div>
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center"><p className="font-display text-[24px] text-amber-700">{audit.counts.missingOnSite}</p><p className="text-[11px] text-amber-700/70 uppercase">Not pulled yet</p></div>
              <div className="rounded-xl bg-red-50 border border-red-100 p-3 text-center"><p className="font-display text-[24px] text-red-600">{audit.counts.staleOnSite}</p><p className="text-[11px] text-red-600/70 uppercase">Stale on site</p></div>
            </div>
          </div>
          <div className="max-h-[360px] overflow-y-auto divide-y divide-[#f5f1ea]">
            {audit.rows.length === 0 ? <p className="p-5 text-[13px] text-[#a0a0a0]">No app listings found to compare.</p> : audit.rows.map((r, i) => {
              const cfg = { synced: { i: CheckCircle2, c: "text-green-600", b: "bg-green-50 text-green-700", l: "Synced" },
                            missing_on_site: { i: ArrowDownToLine, c: "text-amber-600", b: "bg-amber-50 text-amber-700", l: "Not pulled" },
                            stale_on_site: { i: AlertTriangle, c: "text-red-500", b: "bg-red-50 text-red-600", l: "Stale" } }[r.status];
              const Icon = cfg.i;
              return (
                <div key={i} className="flex items-start gap-3 p-3.5" data-testid={`audit-row-${r.status}`}>
                  <Icon size={16} className={`${cfg.c} mt-0.5 shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap"><span className="text-[13px] font-500 text-[#2b2b2b] truncate">{r.title}</span><span className={`text-[10px] px-2 py-0.5 rounded-full ${cfg.b}`}>{cfg.l}</span></div>
                    <p className="text-[12px] text-[#8a8a8a] mt-0.5">{r.reason}</p>
                  </div>
                  <span className="text-[11px] text-[#c0b8a8] font-mono shrink-0">{r.slug}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="rounded-2xl border border-[#e7dcc9] bg-gradient-to-br from-[#fbf8f3] to-white p-5" data-testid="app-sync-headline">
        <div className="grid sm:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1 flex items-center justify-center gap-1"><Smartphone size={13} className="text-[#0A66C2]" /> App is publishing</p>
            <p className="font-display text-[30px] font-500 text-[#2b2b2b]">{status.appListings ?? "—"}</p>
            <p className="text-[11px] text-[#a0a0a0]">{status.appReachable ? "app reachable" : "app unreachable"}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1 flex items-center justify-center gap-1"><RefreshCw size={13} className="text-[#b8935f]" /> Synced to website</p>
            <p className="font-display text-[30px] font-500 text-[#b8935f]">{status.counts?.appSynced ?? 0}</p>
            <p className="text-[11px] text-[#a0a0a0]">last sync: {lastAt}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1 flex items-center justify-center gap-1"><Globe size={13} className="text-[#b8935f]" /> Total live on site</p>
            <p className="font-display text-[30px] font-500 text-[#2b2b2b]">{status.counts?.listings ?? 0}</p>
            <p className="text-[11px] text-[#a0a0a0]">app + website</p>
          </div>
        </div>
        {status.appReachable && status.appListings != null && (status.counts?.appSynced ?? 0) >= status.appListings && (
          <p className="text-[12px] text-green-700 text-center mt-3">✓ In sync — the website holds every listing the app publishes ({status.appListings}), plus your own website listings.</p>
        )}
        {status.appReachable && status.appListings != null && (status.counts?.appSynced ?? 0) < status.appListings && (
          <p className="text-[12px] text-amber-700 text-center mt-3">The app publishes {status.appListings} listing(s) but only {status.counts?.appSynced ?? 0} are synced — hit “Sync now” to pull the rest.</p>
        )}
        {!status.appReachable && (
          <p className="text-[12px] text-[#8a8a8a] text-center mt-3">Couldn’t reach the app API just now — showing the last synced data.</p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1">API key</p><p className="font-display text-[18px] flex items-center gap-1.5">{status.keyConfigured ? <><CheckCircle2 size={16} className="text-green-600" /> Active</> : <><XCircle size={16} className="text-red-500" /> Missing</>}</p></Card>
        <Card><p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1">Webhook</p><p className="font-display text-[18px] flex items-center gap-1.5">{status.webhook?.url ? <><CheckCircle2 size={16} className="text-green-600" /> Registered</> : <><XCircle size={16} className="text-[#c0a878]" /> Not set</>}</p></Card>
        <Card><p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1">Leads</p><p className="font-display text-[26px] text-[#b8935f]">{status.counts.leads}</p></Card>
        <Card><p className="text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1">Users</p><p className="font-display text-[26px] text-[#b8935f]">{status.counts.users}</p></Card>
      </div>

      <Card>
        <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3">Where your data comes from</h4>
        <SourceRow label="Listings" data={status.listingsBySource} />
        <SourceRow label="Leads / enquiries" data={status.leadsBySource} />
        <SourceRow label="Users / accounts" data={status.usersBySource} />
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-3"><Webhook size={16} className="text-[#b8935f]" /><h4 className="font-display text-[16px] font-500 text-[#2b2b2b]">Recent events pushed to the app</h4></div>
        {(!status.recentWebhooks || status.recentWebhooks.length === 0) ? (
          <p className="text-[13px] text-[#a0a0a0]">No webhook events yet. When the app registers its webhook URL, listing and lead changes will show here.</p>
        ) : (
          <div className="space-y-1.5">
            {status.recentWebhooks.map((e, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                <span className="text-[13px] font-500 text-[#2b2b2b]">{e.event}</span>
                <span className="flex items-center gap-3 text-[12px] text-[#a0a0a0]">{e.ref}{e.ok ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-500" />}<span>{new Date(e.at).toLocaleString("en-GB")}</span></span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminAppSync;
